import moment from "moment-timezone";
import { BorderButton } from "src/components/buttons/BorderButton";
import UserModel from "src/models/UserModel";
import { dateFormat } from "src/utils/constants";
import tw from "src/utils/tw";
import { GroupRegistrationStatus } from "types/code-generator";
import LinkButton from "../buttons/LinkButton";
import UserAvatar from "../layout/UserAvatar";
import StudentAge from "./StudentAge";
import ItemCard from "./item/ItemCard";

interface Props {
  user: UserModel;
}

export default function WaitlistAccountItem(props: Props) {
  const waitlistedRegistrations = props.user.groupRegistrations.filter(
    (r) => r.status === GroupRegistrationStatus.Waitlist
  );

  const students = props.user.students
    ?.filter((s) =>
      waitlistedRegistrations.find(
        (r) => r.student?.id === s.id || r.roster?.find((rs) => rs.id === s.id)
      )
    )
    .map((s) => {
      const registrations = waitlistedRegistrations.filter(
        (r) => r.student?.id === s.id || r.roster?.find((rs) => rs.id === s.id)
      );

      return (
        <LinkButton
          className="mb-2 flex gap-4"
          to={`/admin/users/${s.id}`}
          key={s.id}
        >
          <div>
            <UserAvatar user={s} small showInitials />
          </div>
          <div className="flex-1">
            <strong>
              {s.firstName} {s.lastName}
            </strong>
            {s.birthDate && (
              <div className="text-sm text-empty">
                <StudentAge student={s} /> •{" "}
                {moment(s.birthDate).format(dateFormat)} • {s.gender}
              </div>
            )}
            {registrations.length > 0 && (
              <div className="text-sm text-dark">
                {registrations.length} registration
                {registrations.length !== 1 && "s"} on the waitlist
              </div>
            )}
          </div>
        </LinkButton>
      );
    });

  const buttons = (
    <>
      <BorderButton
        type="submit"
        to={`/admin/users/${props.user.id}/enrollments?registrationStatus=enrolled&registrationStatus=waitlist`}
      >
        Manage registration{waitlistedRegistrations.length !== 1 && "s"}
      </BorderButton>
    </>
  );

  return (
    <ItemCard loading>
      <div className="float-right max-w-[50%] text-right">
        <div className="sm:hidden">{buttons}</div>
      </div>

      <div className="mb-3 max-w-sm border-l-4 border-organization bg-gradient-to-r from-lightGray via-white to-white px-4 py-2 text-sm">
        <div className="flex items-center gap-1">
          <div className="w-5 text-center">
            <i
              aria-hidden
              className="far fa-triangle-exclamation text-organization"
            />
          </div>
          <div className="flex-1 text-sm">
            <strong>Waitlisted registrations</strong>
          </div>
        </div>
      </div>

      <LinkButton to={`/admin/users/${props.user.id}`} className="flex gap-2">
        <div>
          <i className="far fa-crown text-organization" />
        </div>
        <div className="flex-1">
          <strong>
            {props.user.firstName} {props.user.lastName}
          </strong>
        </div>
      </LinkButton>

      {props.user.linkedAccounts.map((a) => {
        return (
          <div key={a.id} className="flex gap-2">
            <div>
              <i className="far fa-link-simple text-organization" />
            </div>
            <div className="flex-1">
              {a.firstName} {a.lastName}
            </div>
          </div>
        );
      })}

      <p className="text-sm">
        {waitlistedRegistrations.length} registration
        {waitlistedRegistrations.length !== 1 && "s"} on the waitlist
      </p>

      <div className={tw("border-l-2", "border-borderGray", "pl-4", "mb-5")}>
        {students}
      </div>

      <div className="mt-4 hidden sm:block">{buttons}</div>
    </ItemCard>
  );
}
